// The pitch views, rebuilt for the browser from the mobile geometry.
//
// Percentages throughout, exactly as the React Native components use —
// the pitch is a 2:3 box and everything inside is positioned as a percent
// of it, so the drawing is resolution-independent and matches the phone
// at any preview width.
import {
  BASE_ZONES,
  CHALK,
  PITCH_COLOR as C,
  PITCH_MARKINGS,
  TOKEN_REFERENCE_WIDTH,
  TOKEN_SIZE_PX,
  layoutFor,
  roleLabel,
  zoneById,
  zoneDropPoint,
  type RolePosition,
} from "./geometry";

// ─── Shared frame ───────────────────────────────────────────────────────

function PitchFrame({
  background,
  children,
  width,
}: {
  background: string;
  children: React.ReactNode;
  width: number;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-[14px] border"
      style={{
        width,
        // 2:3 portrait, matching pitchWrap's aspectRatio exactly.
        height: width * 1.5,
        background,
        borderColor: "rgba(255,255,255,0.08)",
      }}
    >
      <ChalkLayer />
      {children}
    </div>
  );
}

// The painted lines, drawn in the same viewBox the phone uses so the
// proportions cannot drift.
function ChalkLayer() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox={CHALK.viewBox}
      preserveAspectRatio="none"
    >
      <rect
        x={CHALK.outer.x}
        y={CHALK.outer.y}
        width={CHALK.outer.w}
        height={CHALK.outer.h}
        stroke={C.chalk}
        strokeWidth={0.6}
        fill="none"
      />
      <line
        x1={CHALK.outer.x}
        y1={CHALK.halfway.y}
        x2={CHALK.outer.x + CHALK.outer.w}
        y2={CHALK.halfway.y}
        stroke={C.chalk}
        strokeWidth={0.6}
      />
      <circle
        cx={CHALK.centreCircle.cx}
        cy={CHALK.centreCircle.cy}
        r={CHALK.centreCircle.r}
        stroke={C.chalk}
        strokeWidth={0.6}
        fill="none"
      />
      {CHALK.boxes.map((b, i) => (
        <rect
          key={i}
          x={b.x}
          y={b.y}
          width={b.w}
          height={b.h}
          stroke={C.chalk}
          strokeWidth={0.6}
          fill="none"
        />
      ))}
    </svg>
  );
}

// Arrows between zone centres. Pulled back from the exact centre and
// given a head, the same way buildConnectorGeometry does on the phone.
function Connectors({
  pairs,
  color = C.gold,
}: {
  pairs: { from: string; to: string }[];
  color?: string;
}) {
  const centre = (id: string) => {
    const z = zoneById(id);
    if (!z) return null;
    // y scaled by 1.5 because the viewBox is 100 x 150.
    return { x: z.left + z.width / 2, y: (z.top + z.height / 2) * 1.5 };
  };

  const geo = pairs
    .map((p, i) => {
      const a = centre(p.from);
      const b = centre(p.to);
      if (!a || !b) return null;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy) || 1;
      const ux = dx / dist;
      const uy = dy / dist;
      const inset = 9;
      const start = { x: a.x + ux * inset, y: a.y + uy * inset };
      const tip = { x: b.x - ux * inset, y: b.y - uy * inset };
      const headLen = 4.5;
      const headW = 2.6;
      const base = { x: tip.x - ux * headLen, y: tip.y - uy * headLen };
      const px = -uy;
      const py = ux;
      return {
        key: `${p.from}-${p.to}-${i}`,
        start,
        base,
        points: [
          `${tip.x},${tip.y}`,
          `${base.x + px * headW},${base.y + py * headW}`,
          `${base.x - px * headW},${base.y - py * headW}`,
        ].join(" "),
      };
    })
    .filter(Boolean) as {
    key: string;
    start: RolePosition;
    base: RolePosition;
    points: string;
  }[];

  if (!geo.length) return null;

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 100 150"
      preserveAspectRatio="none"
    >
      {geo.map((g) => (
        <g key={g.key}>
          <line
            x1={g.start.x}
            y1={g.start.y}
            x2={g.base.x}
            y2={g.base.y}
            stroke={color}
            strokeWidth={1.4}
            strokeDasharray="4,3"
            strokeLinecap="round"
          />
          <polygon points={g.points} fill={color} />
        </g>
      ))}
    </svg>
  );
}

// ─── The nine-zone map ──────────────────────────────────────────────────

export function ZonePitch({
  width,
  highlight = [],
  tone = "correct",
  startZoneId,
  path = [],
  labels = true,
  onZoneClick,
}: {
  width: number;
  highlight?: string[];
  tone?: "correct" | "gold";
  startZoneId?: string;
  /** Ordered hops, for trace-the-pass. */
  path?: string[];
  labels?: boolean;
  // Supplied by the editor. The same component draws the preview and
  // takes the answer, so what an author clicks is literally the thing a
  // learner will tap — there is no second pitch to drift out of step.
  onZoneClick?: (zoneId: string) => void;
}) {
  const chain = [...(startZoneId ? [startZoneId] : []), ...path];
  const pairs = chain.slice(1).map((to, i) => ({ from: chain[i], to }));

  return (
    <PitchFrame width={width} background={C.zonePitchBg}>
      {BASE_ZONES.map((z) => {
        const lit = highlight.includes(z.id);
        const hops = path.flatMap((id, i) => (id === z.id ? [i] : []));
        const isStart = startZoneId === z.id;
        const litColor = tone === "gold" ? C.gold : C.correct;
        const litFill = tone === "gold" ? C.goldFill : C.correctFill;

        return (
          <div
            key={z.id}
            onClick={onZoneClick ? () => onZoneClick(z.id) : undefined}
            className={
              "absolute flex flex-col items-center justify-center rounded-lg" +
              (onZoneClick ? " cursor-pointer hover:brightness-125" : "")
            }
            style={{
              left: `${z.left}%`,
              top: `${z.top}%`,
              width: `${z.width}%`,
              height: `${z.height}%`,
              border: `2px solid ${
                lit || hops.length
                  ? litColor
                  : isStart
                    ? "rgba(255,255,255,0.55)"
                    : "transparent"
              }`,
              borderStyle: isStart && !lit && !hops.length ? "dashed" : "solid",
              background:
                lit || hops.length
                  ? litFill
                  : isStart
                    ? "rgba(255,255,255,0.07)"
                    : "transparent",
              gap: 3,
            }}
          >
            {labels && (
              <span
                className="rounded px-1.5 py-0.5 text-center font-bold leading-tight"
                style={{
                  fontSize: Math.max(7, width * 0.027),
                  color: C.zoneLabel,
                  background: "rgba(0,0,0,0.35)",
                }}
              >
                {z.label}
              </span>
            )}
            {isStart && (
              <span
                className="font-extrabold tracking-wide"
                style={{
                  fontSize: Math.max(6, width * 0.021),
                  color: "rgba(247,250,248,0.75)",
                }}
              >
                BALL STARTS
              </span>
            )}
            {hops.length > 0 && (
              <span className="flex gap-1">
                {hops.map((i) => (
                  <span
                    key={i}
                    className="flex items-center justify-center rounded-full font-extrabold"
                    style={{
                      minWidth: Math.max(14, width * 0.05),
                      height: Math.max(14, width * 0.05),
                      fontSize: Math.max(8, width * 0.03),
                      background: C.gold,
                      color: C.goldInk,
                    }}
                  >
                    {i + 1}
                  </span>
                ))}
              </span>
            )}
          </div>
        );
      })}
      <Connectors pairs={pairs} />
    </PitchFrame>
  );
}

// ─── The tactics board ──────────────────────────────────────────────────

export type PreviewMove = { role: string; to: string; tag?: string };

export function FormationBoard({
  width,
  formation,
  team = "blue",
  highlight = [],
  moves = [],
  rolesToPlace,
  placements,
}: {
  width: number;
  formation: string;
  team?: "blue" | "red";
  highlight?: string[];
  moves?: PreviewMove[];
  /** board-place: roles start off the pitch. */
  rolesToPlace?: string[];
  placements?: Record<string, string>;
}) {
  const layout = layoutFor(formation);
  const tokenSize = Math.max(
    18,
    (TOKEN_SIZE_PX * width) / TOKEN_REFERENCE_WIDTH
  );

  const moved = new Map(moves.map((m) => [m.role, m]));

  const tokens = Object.entries(layout)
    .map(([role, basePos]) => {
      // board-place previews show the answer: each role sits in the zone
      // its key assigns it, not in its formation position.
      if (placements && placements[role]) {
        const drop = zoneDropPoint(placements[role]);
        return drop ? { role, ...drop, moved: true } : null;
      }
      if (rolesToPlace && rolesToPlace.includes(role)) return null;

      const mv = moved.get(role);
      if (mv) {
        const drop = zoneDropPoint(mv.to);
        return drop ? { role, ...drop, moved: true } : null;
      }
      return { role, ...basePos, moved: false };
    })
    .filter(Boolean) as { role: string; x: number; y: number; moved: boolean }[];

  const fill = team === "red" ? C.red : C.blue;
  const ink = team === "red" ? C.redInk : C.blueInk;

  return (
    <PitchFrame width={width} background={C.boardPitchBg}>
      {/* Where a moved player came from, so the movement reads as a
          movement rather than as an odd starting position. */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 100 150"
        preserveAspectRatio="none"
      >
        {moves.map((m) => {
          const from = layout[m.role];
          const to = zoneDropPoint(m.to);
          if (!from || !to) return null;
          return (
            <line
              key={m.role}
              x1={from.x}
              y1={from.y * 1.5}
              x2={to.x}
              y2={to.y * 1.5}
              stroke="rgba(255,215,0,0.75)"
              strokeWidth={0.9}
              strokeDasharray="2,2"
            />
          );
        })}
      </svg>

      {tokens.map((t) => {
        const lit = highlight.includes(t.role);
        return (
          <div
            key={t.role}
            className="absolute flex items-center justify-center rounded-full font-extrabold"
            style={{
              left: `${t.x}%`,
              top: `${t.y}%`,
              width: tokenSize,
              height: tokenSize,
              marginLeft: -tokenSize / 2,
              marginTop: -tokenSize / 2,
              background: lit ? C.gold : fill,
              color: lit ? C.goldInk : ink,
              fontSize: Math.max(8, tokenSize * 0.36),
              border: lit ? `2px solid ${C.gold}` : "none",
              boxShadow: lit ? "0 0 10px rgba(255,215,0,0.6)" : "none",
            }}
          >
            {t.role}
          </div>
        );
      })}
    </PitchFrame>
  );
}

// ─── The painted markings ───────────────────────────────────────────────

export function MarkingsPitch({
  width,
  highlight = [],
  showLabels = true,
  onMarkingClick,
}: {
  width: number;
  highlight?: string[];
  showLabels?: boolean;
  onMarkingClick?: (markingId: string) => void;
}) {
  return (
    <PitchFrame width={width} background={C.boardPitchBg}>
      {PITCH_MARKINGS.map((m) => {
        const lit = highlight.includes(m.id);
        const color = lit ? C.gold : C.chalk;
        const thickness = lit ? 2.5 : 1;

        if (m.shape === "circle") {
          return (
            <div
              key={m.id}
              className="absolute rounded-full"
              style={{
                left: `${m.x}%`,
                top: `${m.y}%`,
                width: `${m.w}%`,
                height: `${m.h}%`,
                border: `${thickness}px solid ${color}`,
              }}
            />
          );
        }
        if (m.shape === "box") {
          return (
            <div
              key={m.id}
              className="absolute"
              style={{
                left: `${m.x}%`,
                top: `${m.y}%`,
                width: `${m.w}%`,
                height: `${m.h}%`,
                border: `${thickness}px solid ${color}`,
              }}
            />
          );
        }
        // Lines are drawn at their authored thickness rather than as a
        // border, so a highlighted line thickens in place instead of
        // shifting by a pixel.
        return (
          <div
            key={m.id}
            className="absolute"
            style={{
              left: `${m.x}%`,
              top: `${m.y}%`,
              width: m.shape === "line-v" ? thickness : `${m.w}%`,
              height: m.shape === "line-h" ? thickness : `${m.h}%`,
              background: color,
            }}
          />
        );
      })}

      {onMarkingClick &&
        PITCH_MARKINGS.map((m) => (
          <div
            key={`hit-${m.id}`}
            onClick={() => onMarkingClick(m.id)}
            title={m.label}
            className="absolute cursor-pointer"
            style={{
              // Lines are hairlines; without a padded hit area they are
              // essentially unclickable.
              left: `calc(${m.x}% - ${m.shape === "line-v" ? 8 : 0}px)`,
              top: `calc(${m.y}% - ${m.shape === "line-h" ? 8 : 0}px)`,
              width:
                m.shape === "line-v" ? 16 : `${m.w}%`,
              height:
                m.shape === "line-h" ? 16 : `${m.h}%`,
              background: "transparent",
            }}
          />
        ))}

      {showLabels &&
        highlight.map((id) => {
          const m = PITCH_MARKINGS.find((x) => x.id === id);
          if (!m) return null;
          return (
            <span
              key={`l-${id}`}
              className="absolute rounded px-1.5 py-0.5 font-bold"
              style={{
                left: `${Math.min(m.x + m.w / 2, 78)}%`,
                top: `${Math.min(m.y + m.h / 2, 94)}%`,
                transform: "translate(-50%, -50%)",
                fontSize: Math.max(7, width * 0.026),
                background: "rgba(8,20,14,0.85)",
                color: C.gold,
                whiteSpace: "nowrap",
              }}
            >
              {m.label}
            </span>
          );
        })}
    </PitchFrame>
  );
}

export { roleLabel };
